/**
 * Community Detail Page
 * Full community profile view with image carousel, info section, and tabbed content
 */

import { Loader2, Search, X } from "lucide-react"
import { useDeferredValue, useEffect, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { ArrowLeftIcon, StarIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useBreakpointItems } from "@/hooks/useBreakpoints"
import { useTranslation } from "@/language"
import { CommunityImageCarousel } from "../components/communityImageCarousel"
import { CommunityInfoSection } from "../components/communityInfoSection"
import { DiscussionTab } from "../components/discussionTab"
import { JoinRequestsSheet } from "../components/JoinRequestsSheet"
import { MembersTab } from "../components/membersTab"
import { RitualistsTab } from "../components/ritualistsTab"
import { RitualsTab } from "../components/ritualsTab"
import { useCommunity, useCommunityStatistics } from "../hooks/useCommunity"
import { useCommunityJoinActions } from "../hooks/useCommunityMembership"
import type { CarouselMediaItem, CommunityDetail } from "../types/community-detail"

interface ApiCommunityMember {
  id: string
  userId: string
  email?: string | null
  name?: string | null
  username?: string | null
  profilePictureUrl?: string | null
  communityBuilder?: boolean
}

type CommunityTab = "rituals" | "discussion" | "ritualists" | "members"

export function CommunityDetailScreen() {
  const { t } = useTranslation("communities")
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialTab = (searchParams.get("tab") as CommunityTab) || "rituals"
  const [activeTab, setActiveTab] = useState<CommunityTab>(initialTab)
  const { join, isJoining } = useCommunityJoinActions()
  const [showSearch, setShowSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const deferredSearchQuery = useDeferredValue(searchQuery)

  // Responsive: vertical on mobile, horizontal on md+
  const isVertical = useBreakpointItems({
    sm: 1,
    md: 0,
    lg: 0,
    xl: 0,
  })

  // Get current user ID from auth store
  const currentUserId = useAuthStore(state => state.user?.id)

  // Fetch real community data
  const { data: apiCommunity, isLoading, isError } = useCommunity(id || "")

  useEffect(() => {
    if (!isLoading && initialTab !== "rituals") {
      const el = document.getElementById("community-tabs")
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100)
      }
    }
  }, [isLoading, initialTab])

  // Fetch community statistics (only for public communities or joined members)
  const { data: statistics } = useCommunityStatistics(id || "", {
    enabled: Boolean(id && (apiCommunity?.type === "PUBLIC" || apiCommunity?.isJoined)),
  })

  // Listen for join click from RitualsTab empty state
  useEffect(() => {
    const handleJoinClick = () => {
      if (id) {
        join(id)
      }
    }
    window.addEventListener("community-join-click", handleJoinClick)
    return () => window.removeEventListener("community-join-click", handleJoinClick)
  }, [join, id])

  // Transform API data to match CommunityDetail type expected by components
  const community: CommunityDetail | null = apiCommunity
    ? {
        id: apiCommunity.id,
        name: apiCommunity.name,
        handle: apiCommunity.handle,
        type: apiCommunity.type,
        description: apiCommunity.description,
        guidelines: apiCommunity.guidelines,
        // Format location from city, state, country
        location:
          [apiCommunity.city, apiCommunity.state, apiCommunity.country]
            .filter(Boolean)
            .join(", ") || undefined,
        // Social handles
        websiteUrl: apiCommunity.websiteUrl || undefined,
        twitterHandle: apiCommunity.twitterUrl || undefined,
        instagramHandle: apiCommunity.instagramUrl || undefined,
        facebookUrl: apiCommunity.facebookUrl || undefined,
        avatarUrl: undefined,
        // Transform uploaded images to cover images array (file paths)
        // Sort to put cover image first, then remaining images
        coverImages:
          apiCommunity.uploadedImages
            ?.slice()
            .sort((a, b) => (b.isCover ? 1 : 0) - (a.isCover ? 1 : 0))
            .map(img => img.filePath) || [],
        // Tags - preserve object structure { id, name }
        tags: apiCommunity.tags || [],
        // Member count from API field, fallback to array length
        memberCount: apiCommunity.totalMemberCount ?? apiCommunity.members?.length ?? 0,
        // Transform members to match CommunityMember interface
        members:
          apiCommunity.members?.map((member: ApiCommunityMember) => ({
            id: member.id,
            userId: member.userId, // Use userId from backend for navigation
            name: member.name || "",
            username: member.username || member.email || "", // Fallback to email if username not available
            avatarUrl: member.profilePictureUrl || undefined, // Use profilePictureUrl from backend
            communityBuilder: member.communityBuilder || false, // Use camelCase
          })) || [],
        // Empty arrays for features without backend implementation
        rituals: [],
        announcements: [],
        discussions: [], // Removed mock data - discussions now come from API if available
        coaches: [], // Removed - no longer used
        createdBy: apiCommunity.createdBy,
        createdAt: apiCommunity.createdAt
          ? new Date(apiCommunity.createdAt).toISOString()
          : undefined,
        // Membership and admin status (from API if available)
        isJoined: apiCommunity.isJoined ?? undefined,
        isPending: apiCommunity.isPending ?? undefined,
        isModerator: apiCommunity.isModerator ?? false,
        isSaved: apiCommunity.isSaved ?? false,
        pendingRequestsCount: apiCommunity.pendingRequestsCount ?? 0,
        permissions: apiCommunity.permissions ?? {
          isOwner: false,
          canManageMembers: false,
          canAddRituals: false,
          canComment: false,
          canReply: false,
        },
      }
    : null

  // Build carousel media - images first (sorted with cover first), then videos
  const carouselMedia: CarouselMediaItem[] = apiCommunity
    ? [
        ...(apiCommunity.uploadedImages
          ?.slice()
          .sort((a, b) => (b.isCover ? 1 : 0) - (a.isCover ? 1 : 0))
          .map(img => ({ type: "image" as const, url: img.filePath })) || []),
        ...(apiCommunity.communityVideos?.map(vid => ({
          type: "video" as const,
          videoUrl: vid.videoUrl,
          thumbnailUrl: vid.thumbnailUrl,
          title: vid.title,
        })) || []),
      ]
    : []

  if (!id) {
    return <div>{t("communityDetail.notFound")}</div>
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-stone">{t("communityDetail.loading")}</p>
      </div>
    )
  }

  if (isError || !community) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-red">{t("communityDetail.error")}</p>
      </div>
    )
  }

  return (
    <div className="w-full mt-5">
      <div className="relative">
        {/* Moderator Join Requests Button - Only show for moderators with pending requests */}
        {community.isModerator && (community.pendingRequestsCount ?? 0) > 0 && (
          <div className="absolute top-20 right-5 md:right-10">
            <JoinRequestsSheet communityId={id || ""} />
          </div>
        )}
      </div>

      {/* Image Carousel */}
      <div className="relative">
        <CommunityImageCarousel
          media={carouselMedia}
          leadingElement={
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate(-1)}
              className="p-5 bg-white/10 backdrop-blur-sm border-white/20 rounded-md"
            >
              <ArrowLeftIcon size="5" color="white" />
            </Button>
          }
        />
      </div>
      <div className="pt-8 md:pt-13  px-5 md:px-17.5 max-w-360 mx-auto">
        {/* Info Section */}
        <CommunityInfoSection community={community} currentUserId={currentUserId} />
      </div>

      {/* Enter the Circle - Promotional Box */}
      {!community.isJoined && !community.isPending && (
        <div className="pt-10 max-w-360 mx-auto px-5 md:px-17.5">
          <div
            className="flex flex-col md:flex-row items-center justify-between p-5 rounded-xl border"
            style={{ backgroundColor: "#C9A8761A", borderColor: "#C9A8761A" }}
          >
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div
                className="flex items-center justify-center w-12 h-12 rounded-full"
                style={{ backgroundColor: "#C9A87626" }}
              >
                <StarIcon className="text-off-white" />
              </div>
              <div className="space-y-1 text-center md:text-left">
                {community.type === "PRIVATE" ? (
                  <>
                    <p className="text-[16px] font-semibold text-primary font-monteserat leading-[20px]">
                      This is a private community
                    </p>
                    <p className="text-[14px] text-stone mt-1 font-monteserat leading-[20px] font-normal">
                      You need an invitation or approval to access this community.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-[16px] font-semibold text-primary font-monteserat leading-[20px]">
                      Every community begins with a small circle
                    </p>
                    <p className="text-[14px] text-stone mt-1 font-monteserat leading-[20px] font-normal">
                      Join early and help create the traditions that others will follow.
                    </p>
                  </>
                )}
              </div>
            </div>
            <Button
              onClick={() => join(community.id)}
              disabled={isJoining}
              variant="primary"
              size="sm"
              className="rounded-full mt-4 md:mt-0"
            >
              {isJoining ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : community.type === "PRIVATE" ? (
                "Request to Join"
              ) : (
                "Enter the Circle"
              )}
            </Button>
          </div>
        </div>
      )}

      <div>
        {/* Only show tabs for public communities OR for joined/pending members of private communities */}
        {community.type === "PUBLIC" || community.isJoined ? (
          <>
            {/* Tabbed Content */}
            <div className="mt-7">
              <Tabs
                id="community-tabs"
                value={activeTab}
                onValueChange={v => setActiveTab(v as CommunityTab)}
                className=""
              >
                <div
                  className={`mb-0 max-w-[1314px] mx-auto w-full flex md:border-b-1 ${isVertical ? "flex-col" : "items-center justify-between"}`}
                >
                  <TabsList
                    variant="default"
                    className={`!h-auto text-body gap-0 p-0 ${isVertical ? "flex-col w-full" : "flex-row"}`}
                  >
                    <TabsTrigger
                      value="rituals"
                      className="rounded-none py-3 md:py-6 md:px-4 px-0 w-full font-semibold font-monteserat justify-center text-[15px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                    >
                      {t("tabs.rituals")} ({statistics?.ritualsAll ?? 0})
                    </TabsTrigger>
                    <TabsTrigger
                      value="discussion"
                      className="rounded-none py-3 md:py-6 md:px-4 px-0 w-full font-semibold font-monteserat justify-center text-[15px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                    >
                      {t("tabs.discussion")} ({statistics?.discussions ?? 0})
                    </TabsTrigger>
                    <TabsTrigger
                      value="ritualists"
                      className="rounded-none py-3 md:py-6 md:px-4 px-0 w-full font-semibold font-monteserat justify-center text-[15px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                    >
                      {t("tabs.ritualists")} ({statistics?.ritualists ?? 0})
                    </TabsTrigger>
                    <TabsTrigger
                      value="members"
                      className="rounded-none py-3 md:py-6 md:px-4 px-0 w-full font-semibold font-monteserat justify-center text-[15px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                    >
                      {t("tabs.members")} ({community.memberCount})
                    </TabsTrigger>
                  </TabsList>

                  <div className="flex items-center md:justify-end">
                    {/* Search toggle (md+ screens) */}
                    <div className="hidden md:flex items-center md:justify-end">
                      {showSearch ? (
                        <div className="mt-5 md:mt-0 flex items-center gap-2 rounded-sm border border-card bg-card px-3 py-1.5 w-full md:w-auto">
                          <Search className="w-4 h-4 text-ash shrink-0" />
                          <Input
                            type="text"
                            placeholder={t("searchPlaceholder")}
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="w-full border-none bg-transparent px-2 text-sm shadow-none focus-visible:ring-0"
                            autoFocus
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="shrink-0 h-6 w-6"
                            onClick={() => {
                              setShowSearch(false)
                              setSearchQuery("")
                            }}
                            aria-label="Close search"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0"
                          onClick={() => setShowSearch(true)}
                          aria-label="Search"
                        >
                          <Search className="!w-5 !h-5 text-body" />
                        </Button>
                      )}
                    </div>
                    {/* Always visible search bar on mobile */}
                    <div className="flex md:hidden items-center gap-2 w-full my-10">
                      <div className="flex items-center gap-2 rounded-sm border border-card bg-card px-3 py-1.5 w-full">
                        <Search className="w-4 h-4 text-ash shrink-0" />
                        <Input
                          type="text"
                          placeholder={t("searchPlaceholder")}
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          className="w-full border-none bg-transparent px-2 text-sm shadow-none focus-visible:ring-0"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tab Contents */}
                <div className="mt-0 bg-card">
                  <div className="max-w-360 mx-auto px-5 md:px-17.5">
                    {/* UPDATED: Rituals tab now accepts searchQuery prop */}
                    <TabsContent value="rituals">
                      <RitualsTab
                        communityId={id}
                        searchQuery={searchQuery}
                        isJoined={community.isJoined}
                        isPending={community.isPending}
                        permissions={community.permissions}
                      />
                    </TabsContent>

                    {/* UPDATED: Discussion tab now accepts searchQuery prop */}
                    <TabsContent value="discussion">
                      <DiscussionTab
                        entityId={community.id}
                        searchQuery={deferredSearchQuery}
                        canComment={community.permissions?.canComment ?? false}
                        canModerate={Boolean(
                          community.permissions?.isOwner || community.permissions?.canManageMembers,
                        )}
                      />
                    </TabsContent>

                    {/* NEW: Ritualists tab */}
                    <TabsContent value="ritualists">
                      <RitualistsTab
                        communityId={id}
                        searchQuery={searchQuery}
                        permissions={community.permissions}
                        currentUserId={currentUserId}
                        creatorId={community.createdBy}
                      />
                    </TabsContent>

                    {/* NEW: Members tab */}
                    <TabsContent value="members">
                      <MembersTab
                        communityId={id}
                        searchQuery={searchQuery}
                        permissions={community.permissions}
                        currentUserId={currentUserId}
                        creatorId={community.createdBy}
                      />
                    </TabsContent>
                  </div>
                </div>
              </Tabs>
            </div>
          </>
        ) : (
          <div className="w-full min-h-[400px] "></div>
        )}
      </div>
    </div>
  )
}
